import React, { useState, useEffect } from "react";
import MaterialTable from "material-table";
import { Row, Col } from "react-bootstrap";
import moment from "moment-timezone";

import { useFormatter } from "../../../util/formatter";
import { useSolarCalc } from "../../../util/calculations";
import Noti from "../../../util/noti";

import "react-datepicker/dist/react-datepicker.css";
import ReceiptModel from "./ReceiptModel";
import ReceiptForm from "./ReceiptForm";

const ReceiptTable = (props) => {
	const [formatNumber] = useFormatter();
	const solarCalc = useSolarCalc();

	const [rows, setRows] = useState(null);
	const [selectedReceipt, setSelectedReceipt] = useState(null);
	const [showForm, setShowForm] = useState(false);
	const [formClass, setFormClass] = useState("");
	const [showPreview, setShowPreview] = useState(false);
	const [isEdit, setIsEdit] = useState(false);

	const initColumns = [
		{ title: "Month", field: "yearMonth", width: 100 },
		{ title: "Entity", field: "entity", width: 50 },
		{ title: "Client", field: "client", width: 300 },
		{
			title: "Site",
			field: "siteName",
			render: (dataObj) => renderMSA(dataObj),
			width: 300,
		},
		{ title: "Invoice No.", grouping: false, field: "invoiceNo", width: 200 },
		{ title: "Amount", grouping: false, field: "amount", width: 150 },
		{ title: "Due Date", grouping: false, field: "dueDate", width: 150 },
		{
			title: "Payment Date",
			grouping: false,
			field: "paymentDate",
			width: 150,
		},
		{
			title: "Aging Days",
			grouping: false,
			field: "agingDays",
			render: (dataObj) => renderAgingDays(dataObj),
			width: 100,
		},
		{
			title: "Actions",
			grouping: false,
			render: (dataObj) => renderActionsCol(dataObj),
			width: 100,
		},
	];

	const [columns, setColumns] = useState(initColumns);

	const tableOpts = {
		showTitle: false,
		grouping: true,
		pageSize: 10,
		pageSizeOptions: [10, 20, 40, 60],
		headerStyle: {
			fontSize: "1rem",
			textAlign: "center",
		},
		cellStyle: {
			textAlign: "center",
		},
	};

	/**
	 * Build table rows from data object
	 * @param {obj} data
	 */
	const buildRows = (data) => {
		const validInvoices = data.filter((v) => v.invoice && v.invoice.length !== 0)
		const msaRows = validInvoices.filter((v) => formatRowObj(v).isMSA_withInfo);
		const codRows = validInvoices.filter((v) => !formatRowObj(v).isMSA);
		const tempRows = msaRows.concat(codRows);
		const rows =
			tempRows.length > 0
				? tempRows.map((dataObj) => formatRowObj(dataObj))
				: [];
		setRows(rows);
	};

	/**
	 * Calculate and return cost object of invoice
	 * @param {obj} invoice
	 * @returns cost object
	 */
	const getProdCost = (invoice) => {
		// Cost calculations
		const prod = {
			ON: invoice.solar_ON,
			OFF: invoice.solar_OFF,
			TOTAL: invoice.solar_total,
		};
		const price = {
			ON: invoice.price_on,
			OFF: invoice.price_off,
			FT: invoice.FT_cost,
		};
		const discount = {
			ON: invoice.ON_discount,
			OFF: invoice.OFF_discount,
			FT: invoice.FT_discount,
		};

		const isBTGSite = invoice.client_id == 69;
		const specialDP = isBTGSite ? 4 : null;

		// Cost calculations results
		const costsObj = solarCalc.calculateProdCost(
			prod,
			price,
			discount,
			invoice.currency,
			specialDP
		);

		return costsObj;
	};

	/**
	 * Format table row object
	 * @param {object} dataObj
	 * @returns table rows object
	 */
	const formatRowObj = (dataObj) => {
		const receipt = dataObj.receipt;
		const invoice = !Array.isArray(dataObj.invoice)
			? dataObj.invoice
			: dataObj.invoice[0];
		const isMSA = invoice.operation_status === 2;
		const isAdditionalInfo = invoice.additional_info !== null;
		const isMSA_withInfo = isMSA && isAdditionalInfo;
		const show_monthly_unitprice =  moment(invoice.dateData).diff(moment("2024-09-01", "YYYY-MM-DD")) >= 0;

		const additional_msa_info = isMSA ? JSON.parse(invoice.additional_info) : null;
		const msa_price = additional_msa_info
			? (show_monthly_unitprice ? invoice.unit_price : additional_msa_info.msa_price.unit_price)
			: null;
		const client = invoice.company;
		const yearMonth = moment(invoice.dateData).format("YYYY-MM");
		const creditTerm = invoice.credit_term;
		const entity = isMSA ? "TH" : invoice.entity_id;
		const invoiceNo = invoice.invoiceNo;

		const siteName = !isMakroReceipt(invoiceNo)
			? isMSA
				? `${invoice.site_name} (MSA)`
				: `${invoice.site_name}`
			: getMakroPhase(invoiceNo);

		// Due date of invoice
		const dueDate = invoice.inv_issued_date == null
			? moment(invoice.dateData).endOf("month").add(creditTerm, "days").format("YYYY-MM-DD")
			: moment(invoice.inv_issued_date).add(creditTerm, "days").format("YYYY-MM-DD");

		// Payment Date
		const paymentDate = receipt.paymentDate != null ? moment(receipt.paymentDate).format("YYYY-MM-DD") : "-";

		// Number of overdue days
		const agingDays = paymentDate === "-" && getAgingDays(dueDate) > 0
			? getAgingDays(dueDate)
			: 0;

		let subtotalPrice = 0;
		let VATPrice = 0;
		let tempTotal = 0;

		// Non-Makro invoices
		if (!Array.isArray(dataObj.invoice)) {
			// Cost calculations results
			const costsObj = getProdCost(invoice);

			// Subtotal price
			subtotalPrice = additional_msa_info
				? msa_price * invoice.msa_day
				: costsObj.costTotal;
			if (isMSA) {
				const extraLineAmount = dataObj.invoice?.special_discount ? parseFloat(dataObj.invoice?.special_discount) : 0;
				subtotalPrice = subtotalPrice + extraLineAmount;
			}

			// Only for sites with BPA fee
			if (invoice.BPA_fee > 0) subtotalPrice = subtotalPrice + invoice.BPA_fee;

			const currency = invoice.currency;
			const vatPercent = invoice.vat;
			// VAT calculations
			const VATObj = solarCalc.calculateVAT(
				subtotalPrice,
				currency,
				vatPercent
			);

			VATPrice = additional_msa_info ? 0.07 * subtotalPrice : VATObj.VAT;
			tempTotal = isMSA ? subtotalPrice + VATPrice : VATObj.totalDue;
		}
		// Makro billing details invoices
		else {
			const invoices = dataObj.invoice;
			// Calculate cost of invoice of each sites
			const invoicesCosts = invoices.map((inv) => getProdCost(inv));

			// Extract only the total
			const totalCosts = invoicesCosts.map((costObj) => costObj.costTotal);

			// Calculate VAT of each sites
			const VATObjs = invoicesCosts.map((costObj) =>
				solarCalc.calculateVAT(costObj.costTotal, invoice.currency, invoice.vat)
			);

			// Extract only the VAT cost
			const VATCosts = VATObjs.map((VATObj) => VATObj.VAT);

			// Sum the prices
			subtotalPrice = totalCosts.reduce((a, b) => a + b);
			VATPrice = VATCosts.reduce((a, b) => a + b);

			// Calculate the total of subtotal and VAT
			tempTotal =
				solarCalc.parseFormatNumber(subtotalPrice, 2) +
				solarCalc.parseFormatNumber(VATPrice, 2);
		}

		let discountPrice = 0;
		let totalDue = 0;
		let specialDiscount_display = "";

		if (invoice.special_discount === null || isMSA) totalDue = tempTotal;
		// Special discounts
		else {
			const specialDiscount = formatNumber(invoice.special_discount * 100, 1);
			specialDiscount_display = `Discount ${specialDiscount}%`;
			discountPrice = -(tempTotal * invoice.special_discount);

			totalDue =
				parseFloat(subtotalPrice.toFixed(2)) +
				parseFloat(VATPrice.toFixed(2)) +
				parseFloat(discountPrice.toFixed(2));
		}

		// Calculated values for multi-sites receipts
		const multiSitePrices = {
			subtotalPrice,
			VATPrice,
			totalDue,
		};

		const result = {
			yearMonth,
			isMSA,
			isMSA_withInfo,
			additional_msa_info,
			entity,
			client,
			siteName,
			invoiceNo,
			amount: formatNumber(totalDue, 2),
			dueDate,
			paymentDate,
			agingDays,
			isMakroReceipt: isMakroReceipt(invoiceNo),
			data: !Array.isArray(dataObj.invoice)
				? dataObj
				: {
					invoice: dataObj.invoice,
					receipt: { ...dataObj.receipt, ...multiSitePrices },
				},
		};

		return result;
	};

	/**
	 * Check if invoice is overdued or not
	 * @param {date} date
	 * @returns boolean
	 */
	const getAgingDays = (date) => moment().diff(moment(date), "days");

	/**
	 * Check if it is a Makro receipt
	 * @param {string} invoiceNo
	 * @returns boolean
	 */
	const isMakroReceipt = (invoiceNo) => invoiceNo.includes("MKO");

	/**
	 * Get Makro Phase
	 * @param {string} invoiceNo
	 * @returns Makro Phase string
	 */
	const getMakroPhase = (invoiceNo) => {
		let phase = 1;
		const invoiceNoSplit = invoiceNo.split("-");
		const siteCode = invoiceNoSplit[1];

		if (siteCode.length > 3) phase = siteCode[3];

		return `Phase ${phase}`;
	};

	/**
	 * Renders button component based on status of the receipt
	 * @param {obj} dataObj
	 * @returns Button component
	 */
	const renderActionsCol = (dataObj) => {
		const isPaid = dataObj.paymentDate != "-";
		const isMissingInfo = dataObj.additional_msa_info == null;
		const isMSA = dataObj.isMSA;

		const initBtn = (
			<button
				title="Issue Receipt"
				className="btn btn-generate btn-invoice"
				onClick={() => initForm(dataObj)}
			>
				<span className="glyphicon glyphicon-pencil"></span>
			</button>
		);
		const showPreviewBtn = (
			<button
				title="Preview Receipt"
				className="btn btn-generate btn-report"
				onClick={() => {
					if (!showPreview) setShowPreview(true);

					setSelectedReceipt(dataObj.data);
				}}
			>
				<span className="glyphicon glyphicon-list-alt"></span>
			</button>
		);

		if (isMSA) {
			return !isMissingInfo && !isPaid ? initBtn : showPreviewBtn;
		} else {
			return !isPaid ? initBtn : showPreviewBtn;
		}
	};

	const renderAgingDays = (dataObj) => {
		const agingDaysNo = dataObj.agingDays;
		const isOverDued = agingDaysNo > 0;

		return isOverDued ? (
			<b style={{ color: "red" }}>{agingDaysNo}</b>
		) : (
			agingDaysNo
		);
	};

	const renderMSA = (dataObj) => {
		return dataObj.isMSA ? (
			<p
				style={{ color: "#ff8500", fontSize: "inherit", fontFamily: "inherit" }}
			>
				{dataObj.siteName}
			</p>
		) : (
			<p style={{ fontSize: "inherit", fontFamily: "inherit" }}>
				{dataObj.siteName}
			</p>
		);
	};
	/**
	 * Reset preview sites
	 */
	const resetPreview = () => {
		setShowPreview(false);
		setSelectedReceipt(null);
	};

	/**
	 * Reset form to its default state
	 */
	const resetForm = () => {
		setShowForm(false);
		setIsEdit(false);
		setSelectedReceipt(null);
		setFormClass("fadeout");
	};

	/**
	 * Initialize the form with selected invoice data
	 * @param {obj} dataObj
	 */
	const initForm = (dataObj) => {
		setShowPreview(false);
		setShowForm(true);
		setSelectedReceipt(dataObj.data);
		setFormClass("fadeIn");
	};

	/**
	 * Display notifications
	 * @param {obj} param0
	 */
	const notify = ({ type, msg }) => {
		const setReload = props.setReload;

		Noti.create({ type, msg });
		setReload(true);
		resetForm();
	};

	useEffect(() => {
		buildRows(props.data);
	}, [props.data]);

	useEffect(() => {
		if (props.reload) setShowPreview(false);
	}, [props.reload]);

	useEffect(() => {
		if (showPreview) {
			setColumns([
				{ title: "Month", field: "yearMonth", width: 100 },
				{ title: "Entity", field: "entity", width: 50 },
				{ title: "Client", field: "client", width: 300 },
				{ title: "Site", field: "siteName", width: 300 },
				{
					title: "Payment Date",
					grouping: false,
					field: "paymentDate",
					width: 200,
				},
				{
					title: "Overdue Days",
					grouping: false,
					field: "agingDays",
					width: 100,
				},
				{
					title: "Actions",
					grouping: false,
					render: (dataObj) => renderActionsCol(dataObj),
					width: 100,
				},
			]);
		} else {
			setColumns(initColumns);
		}
	}, [showPreview]);

	return (
		<>
			{showPreview ? (
				<div>
					<Row>
						<Col md="7">
							<div className="table-style">
								<MaterialTable
									columns={columns}
									data={rows}
									isLoading={props.reload}
									options={{
										tableOpts,
										emptyRowsWhenPaging: false,
										pageSize: 10,
										cellStyle: {
											textAlign: "center",
										},
										headerStyle: {
											fontSize: "1rem",
											textAlign: "center",
										},
									}}
								/>
							</div>
						</Col>
						<Col md="5" className="stick-preview">
							<ReceiptModel
								initForm={initForm}
								resetPreview={resetPreview}
								setIsEdit={setIsEdit}
								selectedReceipt={selectedReceipt}
							/>
						</Col>
					</Row>
				</div>
			) : (
				<div>
					<MaterialTable
						columns={columns}
						data={rows}
						isLoading={props.reload}
						options={{
							tableOpts,
							exportButton: {
								csv: true,
								pdf: false,
							},
							exportAllData: true,
							exportDelimiter: ",",
							exportFileName: "receipts",
							emptyRowsWhenPaging: false,
							pageSize: 10,
							cellStyle: {
								textAlign: "center",
							},
							headerStyle: {
								fontSize: "1rem",
								textAlign: "center",
							},
						}}
					/>
				</div>
			)}

			{showForm && (
				<div className="overlay translucent">
					<div
						id="graphContainer"
						style={{ textAlign: "center", marginTop: "25px", width: "85%" }}
						className={formClass}
					>
						<ReceiptForm
							isEdit={isEdit}
							selectedReceipt={selectedReceipt}
							resetForm={resetForm}
							notify={notify}
						/>
					</div>
				</div>
			)}
			<Noti />
		</>
	);
};

export default ReceiptTable;
